import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt } from "./duct-helpers";

const ResultCard = ({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) => (
  <div className="trade-card flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono-data text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>
      {value} {unit && <span className="text-xs text-muted-foreground font-normal">{unit}</span>}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════
   PSYCHROMETRIC CALCULATOR
   ═══════════════════════════════════════════════════════ */

// Approximate saturation pressure (psi) from temp (°F) using Antoine-like fit
const satPressure = (tF: number): number => {
  const tC = (tF - 32) * 5 / 9;
  // Buck equation (hPa → psi)
  const hPa = 6.1121 * Math.exp((18.678 - tC / 234.5) * (tC / (257.14 + tC)));
  return hPa * 0.0145038;
};

export const PsychrometricCalc = () => {
  const [dryBulb, setDryBulb] = useState("");
  const [rh, setRh] = useState("");

  const db = parseFloat(dryBulb);
  const rhVal = parseFloat(rh);
  const hasInput = !isNaN(db) && rhVal >= 0 && rhVal <= 100;

  const results = useMemo(() => {
    if (!hasInput) return null;
    const patm = 14.696; // sea level psi
    const pSat = satPressure(db);
    const pv = (rhVal / 100) * pSat;
    const W = 0.62198 * pv / (patm - pv); // humidity ratio lb/lb
    const WGr = W * 7000; // grains per lb

    // Dew point (iterative)
    let dpLow = -60, dpHigh = db;
    for (let i = 0; i < 50; i++) {
      const mid = (dpLow + dpHigh) / 2;
      if (satPressure(mid) < pv) dpLow = mid; else dpHigh = mid;
    }
    const dewPoint = (dpLow + dpHigh) / 2;

    // Wet bulb approximation (Stull regression)
    const T = db;
    const R = rhVal;
    const wb = T * Math.atan(0.151977 * Math.sqrt(R + 8.313659))
      + Math.atan(T + R) - Math.atan(R - 1.676331)
      + 0.00391838 * Math.pow(R, 1.5) * Math.atan(0.023101 * R) - 4.686035;

    // Enthalpy (BTU/lb)
    const h = 0.24 * db + W * (1061 + 0.444 * db);

    // Specific volume (ft³/lb)
    const tR = db + 459.67;
    const v = 0.7543 * tR / (patm - pv);

    return { dewPoint, wetBulb: wb, humidityRatio: W, grains: WGr, enthalpy: h, specificVol: v, pSat, pv };
  }, [db, rhVal, hasInput]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-foreground font-semibold">Psychrometric properties</span> from dry bulb temperature and relative humidity at standard atmospheric pressure (sea level).
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Dry Bulb (°F)</label>
          <Input type="number" inputMode="decimal" placeholder="°F…" value={dryBulb} onChange={(e) => setDryBulb(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Relative Humidity (%)</label>
          <Input type="number" inputMode="decimal" placeholder="%…" value={rh} onChange={(e) => setRh(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
      </div>
      {results && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Wet Bulb" value={fmt(results.wetBulb, 1)} unit="°F" accent />
          <ResultCard label="Dew Point" value={fmt(results.dewPoint, 1)} unit="°F" accent />
          <ResultCard label="Humidity Ratio" value={fmt(results.grains, 1)} unit="gr/lb" />
          <ResultCard label="Enthalpy" value={fmt(results.enthalpy, 2)} unit="BTU/lb" />
          <ResultCard label="Specific Volume" value={fmt(results.specificVol, 2)} unit="ft³/lb" />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   COIL SIZING / BTU CALCULATOR
   ═══════════════════════════════════════════════════════ */

export const CoilSizingCalc = () => {
  const [cfm, setCfm] = useState("");
  const [enterTemp, setEnterTemp] = useState("");
  const [leaveTemp, setLeaveTemp] = useState("");
  const [mode, setMode] = useState<"cooling" | "heating">("cooling");

  const cfmVal = parseFloat(cfm);
  const tIn = parseFloat(enterTemp);
  const tOut = parseFloat(leaveTemp);
  const hasInput = cfmVal > 0 && !isNaN(tIn) && !isNaN(tOut);

  const deltaT = Math.abs(tIn - tOut);
  const sensibleBtu = hasInput ? 1.08 * cfmVal * deltaT : 0;
  const tons = sensibleBtu / 12000;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">sensible BTU/h</span> from airflow and temperature differential. Q = 1.08 × CFM × ΔT (sensible heat at standard air density).
        </p>
      </div>
      <div className="space-y-3">
        <div className="flex gap-2">
          <button onClick={() => setMode("cooling")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${mode === "cooling" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>Cooling</button>
          <button onClick={() => setMode("heating")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${mode === "heating" ? "bg-destructive text-destructive-foreground" : "bg-secondary text-muted-foreground"}`}>Heating</button>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Airflow (CFM)</label>
          <Input type="number" inputMode="decimal" placeholder="CFM…" value={cfm} onChange={(e) => setCfm(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Entering Air (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="°F…" value={enterTemp} onChange={(e) => setEnterTemp(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Leaving Air (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="°F…" value={leaveTemp} onChange={(e) => setLeaveTemp(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Sensible Load" value={fmt(sensibleBtu, 0)} unit="BTU/h" accent />
          {mode === "cooling" && <ResultCard label="Cooling Capacity" value={fmt(tons, 2)} unit="tons" accent />}
          <ResultCard label="ΔT" value={fmt(deltaT, 1)} unit="°F" />
          <ResultCard label="Airflow" value={fmt(cfmVal, 0)} unit="CFM" />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CONDENSATE DRAIN SIZING
   ═══════════════════════════════════════════════════════ */

const CONDENSATE_SIZES: { maxTons: number; pipeSize: string }[] = [
  { maxTons: 7.5, pipeSize: "3/4\"" },
  { maxTons: 20, pipeSize: "1\"" },
  { maxTons: 50, pipeSize: "1-1/4\"" },
  { maxTons: 125, pipeSize: "1-1/2\"" },
  { maxTons: 250, pipeSize: "2\"" },
  { maxTons: 500, pipeSize: "2-1/2\"" },
  { maxTons: 1000, pipeSize: "3\"" },
];

export const CondensateDrainCalc = () => {
  const [tons, setTons] = useState("");
  const tonsVal = parseFloat(tons);

  const result = useMemo(() => {
    if (!(tonsVal > 0)) return null;
    for (const s of CONDENSATE_SIZES) {
      if (tonsVal <= s.maxTons) return s;
    }
    return CONDENSATE_SIZES[CONDENSATE_SIZES.length - 1];
  }, [tonsVal]);

  // Approximate condensate GPH (rule of thumb: ~0.12 GPH per ton per hour at full load)
  const gph = tonsVal > 0 ? tonsVal * 0.12 : 0;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Size <span className="text-foreground font-semibold">condensate drain pipes</span> based on cooling system tonnage per UMC/IMC requirements.
        </p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Cooling Capacity (Tons)</label>
        <Input type="number" inputMode="decimal" placeholder="Tons…" value={tons} onChange={(e) => setTons(e.target.value)} className="font-mono-data bg-card border-border" />
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Min. Drain Pipe" value={result.pipeSize} unit="" accent />
          <ResultCard label="Max Capacity" value={String(result.maxTons)} unit="tons" />
          <ResultCard label="Est. Condensate" value={fmt(gph, 2)} unit="GPH" />
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-xs text-muted-foreground">
              <span className="text-foreground font-semibold">Note:</span> Minimum 1/8" per foot slope. Secondary drain or float switch required for ceiling-mounted units.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   DUCT AREA CALCULATOR (Round ↔ Rectangular)
   ═══════════════════════════════════════════════════════ */

export const DuctAreaCalc = () => {
  const [mode, setMode] = useState<"round" | "rect">("round");
  const [diameter, setDiameter] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");

  const diam = parseFloat(diameter);
  const w = parseFloat(width);
  const h = parseFloat(height);

  const roundArea = diam > 0 ? Math.PI * (diam / 2) ** 2 : 0;
  const rectArea = w > 0 && h > 0 ? w * h : 0;
  const aspectRatio = w > 0 && h > 0 ? Math.max(w, h) / Math.min(w, h) : 0;
  // Equivalent round diameter for rectangular
  const eqRound = w > 0 && h > 0 ? 1.3 * Math.pow(w * h, 0.625) / Math.pow(w + h, 0.25) : 0;
  // Equivalent rectangle for round (default 4:3 aspect)
  const eqRectW = diam > 0 ? Math.sqrt((Math.PI * (diam / 2) ** 2) * (4 / 3)) : 0;
  const eqRectH = eqRectW > 0 ? eqRectW * 3 / 4 : 0;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Quick <span className="text-foreground font-semibold">duct area</span> calculation with round ↔ rectangular equivalents.
        </p>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setMode("round")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${mode === "round" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>Round</button>
        <button onClick={() => setMode("rect")} className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${mode === "rect" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>Rectangular</button>
      </div>
      {mode === "round" ? (
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Diameter (in)</label>
          <Input type="number" inputMode="decimal" placeholder="Inches…" value={diameter} onChange={(e) => setDiameter(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Width (in)</label>
            <Input type="number" inputMode="decimal" placeholder="W…" value={width} onChange={(e) => setWidth(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Height (in)</label>
            <Input type="number" inputMode="decimal" placeholder="H…" value={height} onChange={(e) => setHeight(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      )}
      {mode === "round" && diam > 0 && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Area" value={fmt(roundArea, 1)} unit="in²" accent />
          <ResultCard label="Area" value={fmt(roundArea / 144, 3)} unit="ft²" />
          <ResultCard label="Equiv. Rect (4:3)" value={`${fmt(eqRectW, 1)}" × ${fmt(eqRectH, 1)}"`} unit="" />
        </div>
      )}
      {mode === "rect" && w > 0 && h > 0 && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Area" value={fmt(rectArea, 1)} unit="in²" accent />
          <ResultCard label="Area" value={fmt(rectArea / 144, 3)} unit="ft²" />
          <ResultCard label="Equiv. Round ⌀" value={fmt(eqRound, 1)} unit="in" accent />
          <ResultCard label="Aspect Ratio" value={fmt(aspectRatio, 2) + ":1"} unit="" />
          {aspectRatio > 4 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Aspect ratio exceeds 4:1 — increased friction and noise. Consider splitting into dual ducts.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
